// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { GET_INSTRUCTOR_INFO, daysOfWeekLabels } from './schedule.consts.js';

import type {
  HourLabels,
  DaysOfWeekSchedule,
  AccessibleDates,
  NotAccessibleDates,
  Instructor
} from './schedule.types.js';

import { paramsToHash, getTimestamp, formatDate } from '../calendar/calendar.model.js';

import {
  selectHourLabels,
  selectDaysOfWeekSchedule,
  selectAccessibleDates,
  selectNotAccessibleDates
} from './schedule.selectors.js';

import { dateFormatSettings } from '../settings/settings.consts.js';

import { INSTRUCTORS_SCHEDULE } from '../calendar/calendar.consts.js';

import { selectUIMessages } from '../calendar/calendar.selectors.js';

type Props = {|
  UI: $ElementType<ReduxState, 'UI'>,
  event: $ElementType<ReduxState, 'event'>,
  accessgroups: $ElementType<ReduxState, 'accessgroups'>,
  instructor: Instructor,
  hourLabels: HourLabels,
  daysOfWeekSchedule: DaysOfWeekSchedule,
  accessibleDates: AccessibleDates,
  notAccessibleDates: NotAccessibleDates
|};

class InstructorSchedule extends React.Component<Props> {
  static footer = true;

  constructor(props: Props) {
    super(props);
    this.handleClose = this.handleClose.bind(this);
  }

  handleClose: Function;
  handleClose() {
    return this.props.URL.eventBreadcrumbs.length < 2
      ? this.props.paramsToHash({}, this.props.scrollY)
      : this.props.switchModals(this.props.URL.eventBreadcrumbs[0].action, undefined, [
          this.props.URL.eventBreadcrumbs[0]
        ]);
  }

  render() {
    const {
      instructor,
      hourLabels,
      daysOfWeekSchedule,
      accessibleDates,
      notAccessibleDates,
      event,
      user,
      organization,
      accessgroups
    } = this.props;
    if (!instructor || !instructor.id)
      return (
        <input
          type="button"
          style={{ display: 'none' }}
          id={INSTRUCTORS_SCHEDULE}
          onClick={this.handleClose}
        />
      );

    return (
      <div className="instructor-schedule">
        {accessgroups && accessgroups.userReview && event ? (
          <a
            className="underline go-to-link"
            title="Open user in new window"
            href={`${window.serverData._uri}/org/${event.organizationUnique ||
              organization.unique}/users/review/${instructor.id}/`}
            target="_blank"
          >
            Go to instructor's page <span className="icon external-link" />
          </a>
        ) : null}

        <div className="instructor-schedule-panel">
          <div className="instructor-schedule-heading">Instructor's Contacts</div>
          <div className="instructor-schedule-body">
            <div className="instructor-info">
              <b>Name:</b> {instructor.name || '—'}
            </div>
            <div className="instructor-info">
              <b>Email:</b> {instructor.email || '—'}
            </div>
            <div className="instructor-info">
              <b>Work Number:</b> {instructor.workPhoneNumber || '—'}
            </div>
            <div className="instructor-info">
              <b>Mobile Number:</b> {instructor.mobilePhoneNumber || '—'}
            </div>
          </div>
        </div>

        <div className="instructor-schedule-panel">
          <div className="instructor-schedule-heading">Instructor's Schedule</div>
          <div className="instructor-schedule-body">
            <div className="schedule-legend">
              <div className="schedule-legend-color available" />
              <span className="schedule-legend-label">— Working hours</span>
              <div className="schedule-legend-color unavailable" />
              <span className="schedule-legend-label">— Unavailable hours</span>
            </div>
            <div className="info-wrap">
              <div className="schedule-head">
                <div className="schedule-cell" />
                {Object.keys(daysOfWeekSchedule).map(dayOfWeek => (
                  <div key={`label-${dayOfWeek}`} className="schedule-cell">
                    <b>{daysOfWeekLabels[dayOfWeek]}</b>
                  </div>
                ))}
              </div>

              <div className="schedule-grid">
                <div className="schedule-grid-item">
                  {hourLabels.map(label => (
                    <div key={label} className="schedule-cell">
                      <b>{label}</b>
                    </div>
                  ))}
                </div>
                {Object.keys(daysOfWeekSchedule).map(dayOfWeek => (
                  <div className="schedule-grid-item" key={`grid-wrap-${dayOfWeek}`}>
                    {hourLabels.map(label => (
                      <div key={`${label}-grid-${dayOfWeek}`} className="schedule-cell" />
                    ))}
                  </div>
                ))}
              </div>

              <div className="schedule-marking">
                <div className="schedule-marking-item schedule-marking-item-time" />
                {Object.keys(daysOfWeekSchedule).map(dayOfWeek => {
                  const daySchedule = daysOfWeekSchedule[dayOfWeek];

                  return (
                    <div
                      className="schedule-marking-item"
                      title="Unavailable hours"
                      key={`grid-marking-${dayOfWeek}`}
                    >
                      {daySchedule.map(item => (
                        <div key={`${item.offset}-item-${item.height}`} className="item-group">
                          <div
                            className="offset"
                            title="Unavailable hours"
                            style={{ height: `${item.offset / 3}px` }}
                          />
                          <div
                            title="Working hours"
                            className="instructors-working-hours"
                            style={{ height: `${item.height / 3}px` }}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {accessibleDates.length ? (
          <div className="accessible-dates">
            <b>Instructor is accessible</b>
            <ul>
              {accessibleDates.map(date => (
                <li key={`${date.startDate}-${date.finishDate}`}>
                  From{' '}
                  <b>
                    {formatDate(
                      getTimestamp(date.startDate),
                      0,
                      dateFormatSettings[user.settings.date]
                    )}
                  </b>{' '}
                  to{' '}
                  <b>
                    {formatDate(
                      getTimestamp(date.finishDate),
                      0,
                      dateFormatSettings[user.settings.date]
                    )}
                  </b>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {notAccessibleDates.length ? (
          <div className="not-accessible-dates">
            <b>Instructor is not accessible</b>
            <ul>
              {notAccessibleDates.map(date => (
                <li key={`${date.startDate}-${date.finishDate}`}>
                  From{' '}
                  <b>
                    {formatDate(
                      getTimestamp(date.startDate),
                      0,
                      dateFormatSettings[user.settings.date]
                    )}
                  </b>{' '}
                  to{' '}
                  <b>
                    {formatDate(
                      getTimestamp(date.finishDate),
                      0,
                      dateFormatSettings[user.settings.date]
                    )}
                  </b>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <input
          id={INSTRUCTORS_SCHEDULE}
          onClick={this.handleClose}
          type="button"
          style={{ display: 'none' }}
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  UIMessages: selectUIMessages(INSTRUCTORS_SCHEDULE)(state),
  UI: state.UI,
  URL: state.URL,
  event: state.event,
  user: state.user,
  instructor: state.instructor,
  organization: state.organization,
  accessgroups: state.accessgroups,
  hourLabels: selectHourLabels(state),
  daysOfWeekSchedule: selectDaysOfWeekSchedule(state),
  scrollY: state.userUI.scrollY,
  accessibleDates: selectAccessibleDates(state),
  notAccessibleDates: selectNotAccessibleDates(state)
});

export default connect(
  mapStateToProps,
  {
    paramsToHash
  }
)(InstructorSchedule);
